import { useState } from "react";
import type { Form, FormStatus } from "../types";
import IconRecycleBinThrow from "@/components/icons/icon-recycle-bin-throw";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import FormStatusChangeConfirmationModal from "./form-status-change-confirmation-modal";
import FormPublishedLinkModal from "./form-published-link-modal";
import Tooltip from "@/components/ui/tooltip";
import { copyToClipboard } from "@/lib/utils";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";
import { useAuth } from "@/modules/auth";

export interface FormListItemProps {
	form: Form;
	onDelete?: (id: string) => void;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onStatusChange?: (id: string, status: string) => void;
	isStatusChanging?: boolean;
}

const FORM_STATUSES = {
	published: {
		label: "Published",
		value: "published",
	},
	unpublished: {
		label: "Unpublished",
		value: "unpublished",
	},
} as const;

function formatDate(dateString: string) {
	const date = new Date(dateString);
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function StatusDropdown({ status, onRequestStatusChange }: { status: FormStatus; onRequestStatusChange?: (newStatus: FormStatus) => void }) {
	const isPublished = status === FORM_STATUSES.published.value;
	const isUnpublished = status === FORM_STATUSES.unpublished.value;

	if (!onRequestStatusChange) {
		return (
			<span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${isPublished ? "text-green-700" : "text-gray-500"}`}>
				<span className={`w-1.5 h-1.5 rounded-full ${isPublished ? "bg-green-500" : "bg-gray-400"}`} />
				{isPublished ? "Published" : "Unpublished"}
			</span>
		);
	}

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild>
				<button
					type="button"
					className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md cursor-pointer transition-colors hover:bg-gray-100 ${
						isPublished ? "text-green-700" : "text-gray-500"
					}`}
				>
					<span className={`w-1.5 h-1.5 rounded-full ${isPublished ? "bg-green-500" : "bg-gray-400"}`} />
					{isPublished ? "Published" : "Unpublished"}
					<svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</button>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[120px]" sideOffset={5}>
					<DropdownMenu.Item
						className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer outline-none hover:bg-gray-50"
						onSelect={() => {
							if (!isPublished) onRequestStatusChange(FORM_STATUSES.published.value);
						}}
					>
						<span className="text-gray-700">Published</span>
						{isPublished && (
							<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
							</svg>
						)}
					</DropdownMenu.Item>
					<DropdownMenu.Item
						className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer outline-none hover:bg-gray-50"
						onSelect={() => {
							if (!isUnpublished) onRequestStatusChange(FORM_STATUSES.unpublished.value);
						}}
					>
						<span className="text-gray-700">Unpublished</span>
						{isUnpublished && (
							<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
							</svg>
						)}
					</DropdownMenu.Item>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}

export default function FormListItem({ form, onDelete, onEdit, onClone, onStatusChange, isStatusChanging }: FormListItemProps) {
	const { id, slug, title, status, created_at, reviews_count } = form;
	const { currentWorkspaceId } = useAuth();
	const [copied, setCopied] = useState(false);
	const [statusModalOpen, setStatusModalOpen] = useState(false);
	const [pendingStatus, setPendingStatus] = useState<FormStatus | null>(null);
	const [showPublishedLinkModal, setShowPublishedLinkModal] = useState(false);

	// Generate the form URL
	const formUrl = `${import.meta.env.VITE_CLIENT_FORMS_URL}/f/${slug}`;

	const handleRequestStatusChange = (newStatus: FormStatus) => {
		setPendingStatus(newStatus);
		setStatusModalOpen(true);
	};

	const handleConfirmStatusChange = () => {
		if (pendingStatus && onStatusChange) {
			onStatusChange(id, pendingStatus);
			// Show published link modal when publishing
			if (pendingStatus === "published") {
				setShowPublishedLinkModal(true);
			}
		}
		setStatusModalOpen(false);
		setPendingStatus(null);
	};

	const handleCopyLink = async () => {
		try {
			await copyToClipboard(formUrl);
			if (currentWorkspaceId) useOnboardingStore.getState().completeStep(currentWorkspaceId, "share_form");
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	return (
		<div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:shadow-sm hover:border-gray-300 transition-all">
			{/* Left: Title and metadata */}
			<div className="flex items-center gap-4 min-w-0 flex-1">
				{/* Form icon */}
				<div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
					<svg className="w-5 h-5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
							d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
						/>
					</svg>
				</div>

				{/* Title and meta */}
				<div className="min-w-0">
					<h3 className="text-sm font-medium text-primary truncate">{title}</h3>
					<div className="flex items-center gap-3 mt-1">
						<span className="text-xs text-tertiary">Created {formatDate(created_at)}</span>
						<span className="text-xs text-tertiary">•</span>
						<span className="text-xs text-tertiary">
							{reviews_count ?? 0} {reviews_count === 1 ? "response" : "responses"}
						</span>
					</div>
				</div>
			</div>

			{/* Right: Actions */}
			<div className="flex items-center gap-1">
				{/* Status dropdown */}
				<StatusDropdown status={status} onRequestStatusChange={onStatusChange ? handleRequestStatusChange : undefined} />

				{/* Divider */}
				<div className="w-px h-5 bg-gray-200 mx-1" />

				{/* Copy link button */}
				{status === "unpublished" ? (
					<Tooltip content="Link is only available when the form is published">
						<span>
							<button type="button" disabled className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors text-gray-300 cursor-not-allowed">
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
									/>
								</svg>
								<span>Copy Link</span>
							</button>
						</span>
					</Tooltip>
				) : (
					<button
						type="button"
						onClick={handleCopyLink}
						className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors text-tertiary hover:text-primary hover:bg-gray-100"
					>
						{copied ? (
							<>
								<svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
								</svg>
								<span className="text-green-600">Copied!</span>
							</>
						) : (
							<>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
									/>
								</svg>
								<span>Copy Link</span>
							</>
						)}
					</button>
				)}

				{/* Edit button */}
				{onEdit && (
					<button
						type="button"
						onClick={() => onEdit(id)}
						className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-tertiary hover:text-primary hover:bg-gray-100 rounded-md transition-colors"
					>
						<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
							/>
						</svg>
						<span>Edit</span>
					</button>
				)}

				{/* Clone button */}
				{onClone && (
					<button
						type="button"
						onClick={() => onClone(id)}
						className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-tertiary hover:text-primary hover:bg-gray-100 rounded-md transition-colors"
						title="Clone form"
					>
						<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
							/>
						</svg>
						<span>Clone</span>
					</button>
				)}

				{/* Delete button */}
				{onDelete && (
					<button type="button" onClick={() => onDelete(id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" aria-label="Delete form">
						<IconRecycleBinThrow className="w-4 h-4" />
					</button>
				)}
			</div>

			{/* Status change confirmation modal */}
			{pendingStatus && (
				<FormStatusChangeConfirmationModal
					open={statusModalOpen}
					onOpenChange={(open) => {
						setStatusModalOpen(open);
						if (!open) setPendingStatus(null);
					}}
					currentStatus={status}
					newStatus={pendingStatus}
					onConfirm={handleConfirmStatusChange}
					isLoading={isStatusChanging}
				/>
			)}

			{/* Published link modal */}
			<FormPublishedLinkModal open={showPublishedLinkModal} onOpenChange={setShowPublishedLinkModal} formTitle={title} link={formUrl} />
		</div>
	);
}
